import { getComponentErrorHandlingDecision } from 'Diagnostics/componentErrorFilter';
import { setRuntimeLogger } from 'Diagnostics/runtimeLogger';
import { isTranslatedEnvironmentDetected } from 'Diagnostics/translatedEnvironment';
import { storeId as configuredStoreId } from 'Models/serverConfiguration';
import type { RuntimeConfigMap } from 'Models/uiConfig/requestTypes';
import getAnalytics from 'Modules/analytics';
import { RESPONSE_VALIDATION_ERROR_TYPE } from 'Modules/serverApi/common/responseValidationError';
import { isServerStatusPayload } from 'Modules/serverApi/common/statusPayload';
import { getNumberProperty, getStringProperty } from 'Utils/guards';

declare global {
  interface NetworkInformation extends EventTarget {
    readonly effectiveType?: string;
    readonly downlink?: number;
    readonly rtt?: number;
    readonly saveData?: boolean;
  }

  interface Navigator {
    readonly connection?: NetworkInformation;
    readonly mozConnection?: NetworkInformation;
    readonly webkitConnection?: NetworkInformation;
  }
}

/**
 * Client-side diagnostics module.
 *
 * Pushes structured log entries to Grafana Loki via the OTLP JSON endpoint
 * (/otlp/v1/logs) when API requests fall back to the backup server or fail
 * entirely.  Uses the same OTLP format as the server-side .NET services so
 * logs appear with consistent labels (service_name, severity_text, etc.)
 * in Grafana.
 *
 * When a fetch fails, the module collects:
 *  - Original error details (name, message, cause chain)
 *  - Browser network state (online, connection type, RTT, Service Worker)
 *  - Resource Timing data (DNS, TCP, TLS, TTFB when same-origin / TAO)
 *  - Reachability probe (no-cors HEAD to distinguish CORS vs server-down)
 *  - Failure classification (offline, cors, server_unreachable, etc.)
 *
 * Every call is fire-and-forget so it never blocks the UI.
 */

const lokiEndpoint = typeof CONVERMAX_LOKI_ENDPOINT !== 'undefined' ? CONVERMAX_LOKI_ENDPOINT : '';
const otlpAuth = typeof CONVERMAX_OTLP_AUTH !== 'undefined' ? CONVERMAX_OTLP_AUTH : '';

const storeId = configuredStoreId || 'unknown';
const version = typeof CONVERMAX_VERSION !== 'undefined' ? CONVERMAX_VERSION : 'unknown';
const buildTimestamp = typeof CONVERMAX_BUILD_TIMESTAMP !== 'undefined' ? CONVERMAX_BUILD_TIMESTAMP : 0;

type OtlpAttr = { key: string; value: { stringValue: string } | { intValue: number } };

/**
 * Minimal typed shape of the OTLP JSON payload we POST to Loki.
 * Full spec: https://opentelemetry.io/docs/specs/otlp/#otlphttp-request
 * We only model the fields we actually serialize; server accepts supersets.
 */
type OtlpLogsPayload = {
  resourceLogs: readonly {
    resource: { attributes: readonly OtlpAttr[] };
    scopeLogs: readonly {
      scope: { name: string };
      logRecords: readonly {
        timeUnixNano: string;
        severityNumber: number;
        severityText: string;
        body: { stringValue: string };
        attributes: readonly OtlpAttr[];
      }[];
    }[];
  }[];
};
type Reachability = 'reachable' | 'unreachable' | null;

// Lazy analytics handle to resolve circular imports.
let cachedAnalytics: ReturnType<typeof getAnalytics> | null = null;
function getAnalyticsLazy(): ReturnType<typeof getAnalytics> {
  if (cachedAnalytics === null) {
    cachedAnalytics = getAnalytics();
  }
  return cachedAnalytics;
}

// OTLP severity numbers: https://opentelemetry.io/docs/specs/otel/logs/data-model/#severity-fields
// Use .NET-compatible severity text so Dash can parse and filter client logs.
const SEVERITY = {
  INFO: { text: 'Information', number: 9 },
  WARN: { text: 'Warning', number: 13 },
  ERROR: { text: 'Error', number: 17 },
};

const CRAWLER_RE =
  /bot|crawl|spider|slurp|mediapartners|meta-externalads|facebook|twitterbot|linkedinbot|whatsapp|telegram|slack|discordbot|applebot|duckduckbot|baiduspider|yandexbot|semrushbot|ahrefsbot|dotbot|petalbot|mj12bot|seznambot|googleother|lightpanda|headlesschrome/i;

function isCrawlerUserAgent(userAgent?: string | null) {
  return CRAWLER_RE.test(userAgent ?? '');
}

function getCfTraceValue(cfTrace: string, key: string) {
  const prefix = `${key}=`;
  for (const line of cfTrace.split(/\r?\n/)) {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith(prefix)) {
      return trimmedLine.slice(prefix.length).trim();
    }
  }
  return '';
}

function isCrawlerCfTrace(cfTrace?: string | null) {
  if (!cfTrace) {
    return false;
  }
  return isCrawlerUserAgent(getCfTraceValue(cfTrace, 'uag'));
}

/** Cached once at module load - user agent never changes during a page lifetime. */
const isCrawler = isCrawlerUserAgent(navigator.userAgent);

/** @returns {string} current time as nanosecond unix timestamp (string) */
function timestampNs() {
  return `${Date.now()}000000`;
}

/** Helper: build an OTLP string attribute */
function strAttr(key: string, value: unknown): OtlpAttr {
  return { key, value: { stringValue: String(value ?? '') } };
}

/** Helper: build an OTLP int attribute */
function intAttr(key: string, value: number): OtlpAttr {
  return { key, value: { intValue: value } };
}

function errorAttributes(prefix: string, err?: DiagnosticsError | null): OtlpAttr[] {
  if (!err) {
    return [];
  }
  const attrs: OtlpAttr[] = [strAttr(`${prefix}.message`, err.message ?? String(err))];
  if (err.type) {
    attrs.push(strAttr(`${prefix}.type`, err.type));
  }
  if (err.status !== undefined && err.status !== null) {
    attrs.push(intAttr(`${prefix}.status`, err.status));
  }
  // Preserve the raw TypeError/DOMException details from the original fetch error
  const orig = err.originalError;
  if (orig) {
    attrs.push(strAttr(`${prefix}.original_name`, orig.name));
    attrs.push(strAttr(`${prefix}.original_message`, orig.message));
    // error.cause (Chrome 94+, Firefox 91+) may reveal specifics like NetworkError
    if (orig.cause) {
      attrs.push(strAttr(`${prefix}.cause_name`, getStringProperty(orig.cause, 'name') ?? ''));
      attrs.push(
        strAttr(`${prefix}.cause_message`, getStringProperty(orig.cause, 'message') ?? String(orig.cause)),
      );
    }
  }
  if (err.requestUrl) {
    attrs.push(strAttr(`${prefix}.request_url`, err.requestUrl));
  }
  return attrs;
}

// ── browser environment  ────────────────────────────────────────

/**
 * Collect synchronous browser/network context.
 * All reads are synchronous navigator properties — zero overhead.
 */
function browserAttributes() {
  const attrs = [
    strAttr('browser.online', String(navigator.onLine)),
    // Browsers throttle/kill network requests in backgrounded tabs;
    // "hidden" here strongly suggests the fetch failed due to tab backgrounding.
    strAttr('browser.visibility', document.visibilityState),
  ];

  if (isTranslatedEnvironmentDetected()) {
    attrs.push(strAttr('browser.translated_environment_detected', 'true'));
  }

  // Network Information API (Chromium browsers)
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn) {
    if (conn.effectiveType) {
      attrs.push(strAttr('browser.net_type', conn.effectiveType));
    }
    if (conn.downlink !== undefined) {
      attrs.push(strAttr('browser.net_downlink_mbps', String(conn.downlink)));
    }
    if (conn.rtt !== undefined) {
      attrs.push(intAttr('browser.net_rtt_ms', conn.rtt));
    }
    if (conn.saveData) {
      attrs.push(strAttr('browser.save_data', 'true'));
    }
  }

  // Service Worker can silently intercept/modify fetches
  if (navigator.serviceWorker?.controller) {
    attrs.push(strAttr('browser.sw_active', 'true'));
    attrs.push(strAttr('browser.sw_url', navigator.serviceWorker.controller.scriptURL || ''));
  }

  return attrs;
}

/**
 * Extract Resource Timing data for a specific request URL.
 *
 * For cross-origin without Timing-Allow-Origin most phases are zeroed,
 * but entry existence, duration, and nextHopProtocol remain available.
 *
 *  - Entry absent  → request blocked before initiation (CORS preflight,
 *                     CSP, mixed-content, extension)
 *  - Entry present → request was initiated; duration tells how long
 *                     until failure; nextHopProtocol tells if a
 *                     connection was established
 */
function isPerformanceResourceTiming(entry: PerformanceEntry): entry is PerformanceResourceTiming {
  // `initiatorType` is unique to PerformanceResourceTiming; cheap structural check
  // that avoids depending on the global class being present in non-browser envs.
  return 'initiatorType' in entry;
}

function resourceTimingAttributes(url?: string | null): OtlpAttr[] {
  if (!url || typeof performance?.getEntriesByName !== 'function') {
    return [];
  }

  const entries = performance.getEntriesByName(url, 'resource');
  if (!entries.length) {
    return [strAttr('timing.entry_found', 'false')];
  }

  const lastEntry = entries[entries.length - 1]; // most recent entry
  if (!isPerformanceResourceTiming(lastEntry)) {
    return [strAttr('timing.entry_found', 'false')];
  }
  const t = lastEntry;
  const attrs: OtlpAttr[] = [
    strAttr('timing.entry_found', 'true'),
    intAttr('timing.duration_ms', Math.round(t.duration)),
    strAttr('timing.protocol', t.nextHopProtocol || ''),
    intAttr('timing.transfer_size', t.transferSize || 0),
  ];

  // Detailed timing phases (available for same-origin or Timing-Allow-Origin)
  if (t.requestStart > 0) {
    attrs.push(
      intAttr('timing.dns_ms', Math.round(t.domainLookupEnd - t.domainLookupStart)),
      intAttr('timing.tcp_ms', Math.round(t.connectEnd - t.connectStart)),
      intAttr('timing.request_ms', Math.round(t.responseStart - t.requestStart)),
    );
    if (t.secureConnectionStart > 0) {
      attrs.push(intAttr('timing.tls_ms', Math.round(t.connectEnd - t.secureConnectionStart)));
    }
  }

  return attrs;
}

// ── server status  ─────────────────────────────────────────────

const STATUS_URL = 'https://client.convermax.com/status.json';
const CF_TRACE_URL = 'https://1.1.1.1/cdn-cgi/trace';
const PROBE_TIMEOUT_MS = 3000;

let _serverStatus: RuntimeConfigMap | null = null;
let _serverStatusError: string | null = null;
let _statusPromise: Promise<RuntimeConfigMap | null> | null = null;

let _cfTrace: string | null = null;
let _cfTracePromise: Promise<string | null> | null = null;

/**
 * Fetch and cache the primary server's /status.json.
 * Returns the parsed object on success, null on failure.
 * Subsequent calls return the cached result immediately.
 */
async function getServerStatus() {
  if (_serverStatus !== null) {
    return _serverStatus;
  }
  if (_statusPromise) {
    return _statusPromise;
  }

  _statusPromise = (async () => {
    let response;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
      response = await fetch(STATUS_URL, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok) {
        const payload: unknown = await response.json();
        _serverStatus = isServerStatusPayload(payload) ? payload : null;
        return _serverStatus;
      }
      // non-ok response
      _serverStatusError = `HTTP ${response.status} ${response.statusText}`;
    } catch (err) {
      const errorName = err instanceof Error ? err.name : null;
      const errorMessage = err instanceof Error ? err.message : String(err);
      _serverStatusError = errorName === 'AbortError' ? `timeout after ${PROBE_TIMEOUT_MS}ms` : errorMessage;
    }
    return null;
  })();

  return _statusPromise;
}

/**
 * Fetch and cache the Cloudflare trace response (https://1.1.1.1/cdn-cgi/trace).
 * Returns the raw text body on success, null on failure.
 * Subsequent calls return the cached result immediately.
 */
async function getCfTrace() {
  if (_cfTrace !== null) {
    return _cfTrace;
  }
  if (_cfTracePromise) {
    return _cfTracePromise;
  }

  _cfTracePromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
      const response = await fetch(CF_TRACE_URL, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok) {
        _cfTrace = (await response.text()).trim();
        return _cfTrace;
      }
    } catch {
      // swallow – non-critical probe
    }
    return null;
  })();

  return _cfTracePromise;
}

// ── reachability probe ──────────────────────────────────────────

const _reachabilityCache: Partial<Record<string, NonNullable<Reachability>>> = {};

/**
 * Probe whether the request's origin is reachable using a no-cors HEAD.
 *
 * A no-cors request bypasses CORS restrictions — if the origin responds
 * at all (even an opaque response), the server is reachable and the
 * original failure was CORS / response-level, not a network issue.
 *
 * Results are cached per origin for the page lifetime.
 *
 * @returns {Promise<'reachable'|'unreachable'|null>}
 */
async function probeReachability(url?: string | null): Promise<Reachability> {
  if (!url) {
    return null;
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch {
    return null;
  }
  const { origin } = parsedUrl;

  const cachedReachability = _reachabilityCache[origin];
  if (cachedReachability) {
    return cachedReachability;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    await fetch(origin, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    _reachabilityCache[origin] = 'reachable';
  } catch {
    _reachabilityCache[origin] = 'unreachable';
  }

  return _reachabilityCache[origin] ?? null;
}

// ── failure classification ──────────────────────────────────────

/**
 * Best-effort classification of why a request failed.
 * Combines error properties, browser state, and probe results into
 * a single human-readable label for the `failure.likely_cause` attribute.
 */
function classifyFailure(error?: DiagnosticsError | null, reachability?: Reachability): string {
  if (!error) {
    return 'no_error';
  }
  if (error.type === RESPONSE_VALIDATION_ERROR_TYPE) {
    return 'response_validation';
  }

  // Known typed errors
  if (error.message?.includes('timeout')) {
    return 'timeout';
  }
  if (error.type === 'Request Aborted') {
    return 'aborted';
  }

  // HTTP errors — server responded
  const { status, requestUrl } = error;
  if (status !== undefined && status >= 500) {
    return 'server_error';
  }
  if (status !== undefined && status >= 400) {
    return 'client_error';
  }
  if (status !== undefined && status > 0) {
    return `http_${status}`;
  }

  // Network-level failure analysis
  if (!navigator.onLine) {
    return 'browser_offline';
  }

  // Mixed content (HTTPS page → HTTP API)
  if (requestUrl && location.protocol === 'https:') {
    try {
      if (new URL(requestUrl).protocol === 'http:') {
        return 'mixed_content';
      }
    } catch {
      /* malformed URL */
    }
  }

  // Reachability probe narrows CORS vs server-down
  if (reachability === 'reachable') {
    return 'cors_or_blocked';
  }
  if (reachability === 'unreachable') {
    return 'server_unreachable';
  }

  return 'unknown_network';
}

/** Map server status fields to OTLP resource attributes. */
function serverStatusAttributes(status?: RuntimeConfigMap | null): OtlpAttr[] {
  if (!status) {
    return _serverStatusError ? [strAttr('server.status_error', _serverStatusError)] : [];
  }
  return [strAttr('server.instance_id', status.InstanceId), strAttr('network.network_as', status.ClientAS)];
}

// ── rate limiting ───────────────────────────────────────────────────────

/** Global cap on log entries per page load to prevent flooding Loki. */
let _logCount = 0;
const MAX_LOGS_PER_PAGE = 50;

// ── sanitization ────────────────────────────────────────────────────

/** Truncate a string to a maximum byte-ish length (for stack traces). */
function truncate(str: string | null | undefined, maxLen = 4096): string {
  if (!str || str.length <= maxLen) {
    return str ?? '';
  }
  return `${str.slice(0, maxLen)}...[truncated]`;
}

/**
 * Push a log record to Loki via OTLP JSON (/otlp/v1/logs).
 *
 * Mirrors the resource/scope/logRecord structure that the .NET
 * OpenTelemetry SDK produces so Loki indexes the same labels:
 *   service_name = "Client"
 *   service_namespace = "javascript"
 *   severity_text = "Information" | "Warning" | "Error"
 *
 * @param {object}  severity
 * @param {string}  message
 * @param {Array}   attributes   Caller-built OTLP attributes.
 * @param {object}  [failureCtx] Optional: { error, requestUrl } for
 *                                fetch-failure logs.  Triggers the
 *                                reachability probe and classification.
 */
async function pushOtlpLog(
  severity: { text: string; number: number },
  message: string,
  attributes: OtlpAttr[],
  failureCtx?: { error?: DiagnosticsError | null; requestUrl?: string },
): Promise<void> {
  if (!lokiEndpoint || !otlpAuth) {
    return;
  }

  // Global rate limit – prevent flooding in catastrophic loops
  if (++_logCount > MAX_LOGS_PER_PAGE) {
    return;
  }

  // Run all probes in parallel; reachability only for network-level failures
  const requestUrl = failureCtx?.requestUrl;
  const shouldProbe = requestUrl && !failureCtx?.error?.status;

  const [status, cfTrace, reachability] = await Promise.all([
    getServerStatus(),
    getCfTrace(),
    shouldProbe ? probeReachability(requestUrl) : Promise.resolve(null),
  ]);

  if (isCrawlerCfTrace(cfTrace)) {
    return;
  }

  const resourceAttrs = [
    strAttr('service.name', 'Client'),
    strAttr('service.namespace', 'javascript'),
    strAttr('service.version', version),
    intAttr('build.version_timestamp', buildTimestamp),
    strAttr('store.id', storeId),
    strAttr('browser.user_agent', navigator.userAgent),
    ...serverStatusAttributes(status),
  ];
  if (cfTrace) {
    resourceAttrs.push(strAttr('network.cf_trace', cfTrace));
  }

  // Build log-record attributes: page context → caller attrs → probe results
  const logAttrs = [
    strAttr('page.url', window.location.href),
    strAttr('page.referrer', document.referrer),
    ...browserAttributes(),
    ...attributes,
  ];
  const analytics = getAnalyticsLazy();
  if (analytics.userId) {
    logAttrs.push(strAttr('user.id', analytics.userId));
  }

  if (reachability) {
    logAttrs.push(strAttr('probe.reachability', reachability));
  }
  if (failureCtx?.error) {
    logAttrs.push(strAttr('failure.likely_cause', classifyFailure(failureCtx.error, reachability)));
  }

  const otlpPayload = {
    resourceLogs: [
      {
        resource: {
          attributes: resourceAttrs,
        },
        scopeLogs: [
          {
            scope: { name: 'diagnostics' },
            logRecords: [
              {
                timeUnixNano: timestampNs(),
                severityNumber: severity.number,
                severityText: severity.text,
                body: { stringValue: message },
                attributes: logAttrs,
              },
            ],
          },
        ],
      },
    ],
  } satisfies OtlpLogsPayload;
  const payload = JSON.stringify(otlpPayload);

  try {
    fetch(`${lokiEndpoint}/otlp/v1/logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${btoa(otlpAuth)}`,
      },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // silently swallow – diagnostics must never break the app
  }
}

// ── deduplication ───────────────────────────────────────────────────────

/** Set of error keys already reported this page load. */
const _reportedErrors = new Set<string>();

/**
 * Returns true the first time a given key is seen; false after that.
 * Prevents flooding Loki when the same template error fires for every
 * item on the page.
 */
function shouldReport(key: string) {
  if (_reportedErrors.has(key)) {
    return false;
  }
  _reportedErrors.add(key);
  return true;
}

// ── public API ──────────────────────────────────────────────────────────

export interface DiagnosticsError {
  name?: string;
  message?: string;
  stack?: string;
  cause?: unknown;
  requestUrl?: string;
  status?: number;
  type?: string;
  originalError?: Error;
}

/**
 * The raw input to diagnostics-error helpers.
 *
 * Always `unknown` — callers pass whatever they caught (thrown value,
 * rejected promise reason, Error instance, plain object, string,
 * etc.). `normalizeDiagnosticsError` is the single choke-point that
 * produces a typed `DiagnosticsError` for downstream consumers.
 */
type DiagnosticsErrorInput = unknown;

interface DiagnosticsContextBase {
  error?: DiagnosticsErrorInput;
}

interface NetworkDiagnosticsContext {
  endpoint?: string;
  scenario?: 'backup_used' | 'backup_failed' | 'request_error';
  primaryError?: DiagnosticsErrorInput;
  backupError?: DiagnosticsErrorInput;
}

interface RuntimeDiagnosticsContext extends DiagnosticsContextBase {
  source?: string;
  extra?: Record<string, string>;
}

interface TemplateDiagnosticsContext extends DiagnosticsContextBase {
  componentName?: string;
  itemId?: string | number;
}

interface ComponentDiagnosticsContext {
  componentName?: string;
  error?: Error;
  componentStack?: string;
}

interface EpicDiagnosticsContext extends DiagnosticsContextBase {
  epicName?: string;
  action?: string;
}

export function normalizeDiagnosticsError(error: DiagnosticsErrorInput): DiagnosticsError | undefined {
  if (error == null) {
    return undefined;
  }

  if (typeof error !== 'object') {
    return { message: String(error) };
  }

  const isNativeError = error instanceof Error;
  const rawOriginal = 'originalError' in error ? error.originalError : undefined;
  return {
    cause: 'cause' in error ? error.cause : undefined,
    message: getStringProperty(error, 'message') ?? String(error),
    name: getStringProperty(error, 'name'),
    originalError: rawOriginal instanceof Error ? rawOriginal : isNativeError ? error : undefined,
    requestUrl: getStringProperty(error, 'requestUrl'),
    stack: getStringProperty(error, 'stack'),
    status: getNumberProperty(error, 'status'),
    type: getStringProperty(error, 'type'),
  };
}

/**
 * Unified network/API request failure logger.
 *
 * Severity: always Warning. HTTP 429 on either leg is suppressed entirely
 * (rate-limit noise is not actionable). The reachability probe and failure
 * classification in pushOtlpLog still run for network-level errors.
 */
export function logNetworkRequest({
  endpoint,
  scenario,
  primaryError,
  backupError,
}: NetworkDiagnosticsContext): void {
  const normalizedPrimaryError = normalizeDiagnosticsError(primaryError);
  const normalizedBackupError = normalizeDiagnosticsError(backupError);

  if (isCrawler) {
    return;
  }
  if (normalizedPrimaryError?.status === 429 || normalizedBackupError?.status === 429) {
    return;
  }
  const requestUrl = normalizedPrimaryError?.requestUrl;
  const hasResponseValidationError =
    normalizedPrimaryError?.type === RESPONSE_VALIDATION_ERROR_TYPE ||
    normalizedBackupError?.type === RESPONSE_VALIDATION_ERROR_TYPE;
  const severity = hasResponseValidationError ? SEVERITY.ERROR : SEVERITY.WARN;
  const message =
    scenario === 'backup_used'
      ? `Primary request failed for ${endpoint}, backup succeeded`
      : scenario === 'backup_failed'
        ? `Both primary and backup requests failed for ${endpoint}`
        : `Request failed for ${endpoint}`;

  if (scenario === 'backup_failed') {
    console.error('[cm-diag] BOTH primary and backup failed', endpoint, {
      primary: normalizedPrimaryError?.message,
      backup: normalizedBackupError?.message,
    });
  } else {
    console.warn('[cm-diag] network request failed', endpoint, normalizedPrimaryError?.message);
  }

  const attrs = [
    strAttr('scenario', scenario),
    strAttr('endpoint', endpoint),
    ...errorAttributes('primary', normalizedPrimaryError),
    ...(normalizedBackupError ? errorAttributes('backup', normalizedBackupError) : []),
    ...resourceTimingAttributes(requestUrl),
  ];

  pushOtlpLog(severity, message, attrs, { error: normalizedPrimaryError, requestUrl });
}

/**
 * Log a template rendering error (e.g. .rt template threw during render).
 *
 * Deduplicates by error message so that a single broken template producing
 * N identical errors (one per item on the page) only sends one log entry.
 *
 * @param {{ componentName: string, itemId: string|number, error: unknown }} params
 *   `error` is whatever `safeTemplateCall`'s catch received — typically an
 *   `Error` instance but may be any thrown value. `normalizeDiagnosticsError`
 *   handles all shapes.
 */
export function logTemplateError({ componentName, itemId, error }: TemplateDiagnosticsContext): void {
  const normalizedError = normalizeDiagnosticsError(error);
  const message = normalizedError?.message ?? String(error);
  const dedupKey = `template:${componentName}:${message}`;

  if (isCrawler || !shouldReport(dedupKey)) {
    return;
  }

  pushOtlpLog(SEVERITY.ERROR, `Template rendering failed in ${componentName}`, [
    strAttr('scenario', 'template_error'),
    strAttr('template.component', componentName),
    strAttr('template.item_id', String(itemId ?? '')),
    strAttr('error.message', message),
    strAttr('error.stack', truncate(normalizedError?.stack)),
  ]);
}

/**
 * Log a general runtime error (uncaught exception, unhandled rejection, etc.).
 *
 * Deduplicates by source + error message.
 *
 * @param {{ source: string, error: Error|unknown, extra?: Record<string, string> }} params
 */
export function logRuntimeError({ source, error, extra }: RuntimeDiagnosticsContext): void {
  const err = normalizeDiagnosticsError(error) ?? { message: 'unknown' };
  const message = err.message ?? String(error ?? 'unknown');
  const dedupKey = `runtime:${source}:${message}`;

  if (isCrawler || !shouldReport(dedupKey)) {
    return;
  }

  const attrs = [
    strAttr('scenario', source),
    strAttr('error.name', err.name ?? ''),
    strAttr('error.message', message),
    strAttr('error.stack', truncate(err.stack)),
  ];

  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      attrs.push(strAttr(k, v));
    }
  }

  pushOtlpLog(SEVERITY.ERROR, `Runtime error [${source}]: ${message}`, attrs);
}

// Register the OTLP-backed implementation with the cycle-safe
// `runtimeLogger` registry. Modules that live on the
// `componentlessWidgetsConfig -> widgetProcessor -> diagnostics ->
// requestConfig -> componentlessWidgetsConfig` evaluation cycle
// import `logRuntimeError` from `Diagnostics/runtimeLogger`; once
// this module finishes loading, those calls flow through Loki
// instead of `console.error`.
setRuntimeLogger(logRuntimeError);

/**
 * Log a React component error caught by an Error Boundary.
 *
 * Deduplicates by component name + error message.
 *
 * @param {{ componentName: string, error: Error, componentStack?: string }} params
 */
export function logComponentError({
  componentName,
  error,
  componentStack,
}: ComponentDiagnosticsContext): void {
  const message = error?.message ?? String(error);
  const dedupKey = `component:${componentName}:${message}`;
  const decision = getComponentErrorHandlingDecision({
    componentName,
    error,
    componentStack,
    translatedEnvironmentDetected: isTranslatedEnvironmentDetected(),
  });

  if (isCrawler || decision.action === 'drop' || !shouldReport(dedupKey)) {
    return;
  }

  const severity = decision.action === 'warn' ? SEVERITY.WARN : SEVERITY.ERROR;

  pushOtlpLog(severity, `React component error in ${componentName}`, [
    strAttr('scenario', 'component_error'),
    strAttr('component.name', componentName),
    strAttr('error.name', error?.name ?? ''),
    strAttr('error.message', message),
    strAttr('error.stack', truncate(error?.stack)),
    strAttr('error.component_stack', truncate(componentStack)),
  ]);
}

/**
 * Log an error thrown from a Redux epic.
 *
 * Deduplicates by epic name + error message.
 *
 * @param {{ epicName: string, action: string, error: Error|unknown }} params
 */
export function logEpicError({ epicName, action, error }: EpicDiagnosticsContext): void {
  const err = normalizeDiagnosticsError(error) ?? { message: 'unknown' };
  const message = err.message ?? String(error ?? 'unknown');
  const dedupKey = `epic:${epicName}:${message}`;

  if (isCrawler || !shouldReport(dedupKey)) {
    return;
  }

  pushOtlpLog(SEVERITY.ERROR, `Epic error in ${epicName}`, [
    strAttr('scenario', 'epic_error'),
    strAttr('epic.name', epicName),
    strAttr('action.type', action ?? ''),
    strAttr('error.name', err.name ?? ''),
    strAttr('error.message', message),
    strAttr('error.stack', truncate(err.stack)),
  ]);
}
